import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Stepper — wizard progress indicator.
 *
 * Used by the New Order wizard:
 *   1. Patient → 2. Line type → 3. Prescription → 4. Review
 *
 * RTL: connector segments use border-inline-start; orientation logic is
 *      handled by the row layout (flex-row in `dir="rtl"` reverses naturally).
 */

export interface Step {
  id: string;
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: Step[];
  currentStep: number;
  /** completed indices (0-based) */
  completed?: number[];
  className?: string;
  onStepClick?: (index: number) => void;
}

export function Stepper({
  steps,
  currentStep,
  completed = [],
  onStepClick,
  className,
}: StepperProps) {
  return (
    <div
      role="list"
      aria-label="Progress"
      // R28 — flat flex layout: steps render at natural width (shrink-0),
      // connectors are siblings (not children of li) so every connector gets
      // its OWN flex-1 share of the leftover space. Result: connector lines
      // are equal length regardless of label length, and the overall row
      // stays at constant height across branches.
      className={cn('flex items-center gap-2 w-full', className)}
    >
      {steps.map((step, idx) => {
        const isCompleted = completed.includes(idx) || idx < currentStep;
        const isCurrent = idx === currentStep;
        const isClickable = onStepClick && (isCompleted || idx <= currentStep);

        return (
          <React.Fragment key={step.id}>
            <div role="listitem" className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick(idx)}
                // Stable accessible name: the visible label is a sibling <p>
                // (hidden on mobile for non-current steps, and replaced by a
                // bare check icon when completed), so without this the step
                // buttons are unnamed for screen readers.
                aria-label={step.label}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold',
                  'transition-all duration-fast ease-out',
                  isCompleted &&
                    'bg-brand-500 text-text-onBrand hover:bg-brand-600',
                  isCurrent && !isCompleted &&
                    'bg-brand-50 text-brand-700 ring-2 ring-brand-500',
                  !isCompleted && !isCurrent &&
                    'bg-sand-100 text-text-muted',
                  isClickable && 'cursor-pointer focus-visible:outline-none',
                  !isClickable && 'cursor-default',
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : idx + 1}
              </button>
              {/* Mobile (<640px): only the CURRENT step keeps its label —
                  5 nowrap labels can't fit a phone width and used to push
                  later steps (incl. Review) off-canvas. Circles always
                  render, so progress stays visible. Desktop unchanged. */}
              <div className={cn('flex flex-col', !isCurrent && 'max-sm:hidden')}>
                <p
                  className={cn(
                    'text-sm font-medium whitespace-nowrap',
                    isCurrent ? 'text-text-primary' : 'text-text-secondary',
                  )}
                >
                  {step.label}
                </p>
                {step.description ? (
                  <p className="text-xs text-text-muted mt-0.5 whitespace-nowrap">
                    {step.description}
                  </p>
                ) : null}
              </div>
            </div>
            {idx < steps.length - 1 ? (
              <div
                aria-hidden="true"
                className={cn(
                  'flex-1 h-px transition-colors duration-standard',
                  isCompleted ? 'bg-brand-300' : 'bg-border-subtle',
                )}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}
